import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import RevealProvider from '../components/RevealProvider';
import { recommendCountries, formatUSD } from '../data/countries';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  {
    key: 'income',
    eyebrow: 'Step 1 of 5',
    title: 'What’s your current monthly income?',
    desc: 'From remote work — employment, freelance, or a business you own. We’ll match you to programmes where you comfortably clear the threshold.',
    options: [
      { v: 1500, label: 'Under $2,000 / mo', sub: 'Room for low-threshold programmes' },
      { v: 3000, label: '$2,000 – $4,000 / mo', sub: 'Fits most LatAm + SE Asia programmes' },
      { v: 5500, label: '$4,000 – $7,000 / mo', sub: 'Opens Europe + Asia majors' },
      { v: 9000, label: '$7,000 – $12,000 / mo', sub: 'All programmes comfortable' },
      { v: 15000, label: 'Over $12,000 / mo', sub: 'You qualify for every programme we work with' },
    ],
  },
  {
    key: 'workType',
    eyebrow: 'Step 2 of 5',
    title: 'How do you earn?',
    desc: 'Some visas prefer W-2 / employment contracts; others favour self-employed / freelance income.',
    options: [
      { v: 'employee', label: 'I’m a remote employee', sub: 'W-2, salary from one foreign employer' },
      { v: 'freelancer', label: 'I’m a remote freelancer or founder', sub: 'Multiple clients, 1099, or my own business' },
      { v: 'both', label: 'A bit of both', sub: 'Mixed employment + side work' },
    ],
  },
  {
    key: 'family',
    eyebrow: 'Step 3 of 5',
    title: 'Who’s moving with you?',
    desc: 'Spouse and dependent income thresholds vary meaningfully by country.',
    options: [
      { v: 'solo', label: 'Just me', sub: 'Solo application' },
      { v: 'couple', label: 'Me and my partner', sub: 'Spouse / partner joining' },
      { v: 'family', label: 'With children', sub: 'Family of 3+' },
    ],
  },
  {
    key: 'regionPreference',
    eyebrow: 'Step 4 of 5',
    title: 'Any preferred region?',
    desc: 'Pick one we’ll prioritise, or leave open and we’ll recommend across the board.',
    options: [
      { v: 'any', label: 'Open to anywhere', sub: 'Surprise me — best fit wins' },
      { v: 'europe', label: 'Europe', sub: 'EU / Schengen / Balkans' },
      { v: 'asia', label: 'Asia', sub: 'Southeast, East, or South Asia' },
      { v: 'americas', label: 'Americas', sub: 'Latin America, Caribbean, USD-strong' },
      { v: 'middle_east', label: 'Middle East', sub: 'Tax-free bases' },
      { v: 'africa', label: 'Africa', sub: 'Indian Ocean + continental Africa' },
    ],
  },
  {
    key: 'tax',
    eyebrow: 'Step 5 of 5',
    title: 'How important is favourable tax treatment?',
    desc: 'Some programmes offer zero tax on foreign income; others treat you as a regular tax resident.',
    options: [
      { v: 'yes', label: 'Very important', sub: 'Bias me toward tax-friendly programmes' },
      { v: 'meh', label: 'Nice to have, not critical', sub: 'Lifestyle beats tax' },
      { v: 'no', label: 'Not a factor', sub: 'I’ll handle tax separately' },
    ],
  },
];

export default function Quiz(){
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [email, setEmail] = useState('');

  const step = STEPS[stepIdx];

  const select = (val) => setAnswers(a => ({ ...a, [step.key]: val }));
  const next = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    else finish();
  };
  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const finish = async () => {
    const recs = recommendCountries({
      monthlyIncomeUsd: answers.income,
      workType: answers.workType,
      family: answers.family,
      regionPreference: answers.regionPreference,
      tax: answers.tax,
    });
    setResults(recs);
    // Save quiz submission in background (no failure surfaced to user)
    try {
      await axios.post(`${API}/quiz`, {
        monthly_income_usd: answers.income,
        work_type: answers.workType,
        family: answers.family,
        region_preference: answers.regionPreference,
        recommendations: recs.map(r => r.country.slug),
        email: email || undefined,
      });
    } catch (e) { /* ignore */ }
  };

  if (results){
    return (
      <RevealProvider>
        <Helmet>
          <title>Your Visa Matches — Nomad Compass Quiz</title>
          <meta name="description" content="Your top 3 digital nomad visa matches based on your income, work type, and family situation." />
          <link rel="canonical" href="https://nomadcompasshub.com/quiz" />
        </Helmet>
        <TopBar variant="page" />
        <div className="quiz-page">
          <div className="quiz-wrap quiz-results">
            <div className="quiz-eyebrow">Your matches</div>
            <h2>Based on your answers, these <em>3 countries fit best.</em></h2>
            <p className="quiz-desc" style={{ marginBottom: 32 }}>Ranked by income fit, regional preference, and visa validity. Tap any country to see its full programme.</p>
            {results.map((r, i) => (
              <Link key={r.country.slug} to={`/countries/${r.country.slug}`} className="quiz-result-card" data-testid={`result-${i}`}>
                <div className="quiz-result-image"><img src={r.country.image} alt={r.country.name} /></div>
                <div>
                  <div className="quiz-result-rank">Match #{i + 1} · {r.country.flag}</div>
                  <div className="quiz-result-name">{r.country.name}</div>
                  <div className="quiz-result-meta">{r.country.visaName} · {r.country.regionLabel}</div>
                  <div className="quiz-result-stats">
                    <span>Income req <strong>{formatUSD(r.effectiveIncome)}/mo</strong></span>
                    <span>Processing <strong>{r.country.processing}</strong></span>
                    <span>Validity <strong>{r.country.validity}</strong></span>
                  </div>
                </div>
                <span className="quiz-result-cta">Explore →</span>
              </Link>
            ))}
            <div className="quiz-buttons" style={{ marginTop: 40 }}>
              <button className="btn-secondary" onClick={() => { setResults(null); setStepIdx(0); setAnswers({}); }}>← Retake quiz</button>
              <Link to="/#enquiry" className="btn-primary" data-testid="quiz-apply">Start an application</Link>
            </div>
          </div>
        </div>
        <Footer />
      </RevealProvider>
    );
  }

  const selected = answers[step.key];
  const canProceed = selected !== undefined && selected !== null;

  return (
    <RevealProvider>
      <Helmet>
        <title>Digital Nomad Visa Quiz — Find Your Best Match | Nomad Compass</title>
        <meta name="description" content="Take the 60-second nomad visa quiz. Tell us your income, work style, and family situation and we’ll match you to the best 3 countries." />
        <link rel="canonical" href="https://nomadcompasshub.com/quiz" />
      </Helmet>
      <TopBar variant="page" />
      <div className="quiz-page">
        <div className="quiz-wrap">
          <div className="quiz-progress" aria-hidden="true">
            {STEPS.map((_, i) => (
              <div key={i} className={`quiz-dot ${i < stepIdx ? 'done' : i === stepIdx ? 'active' : ''}`} />
            ))}
          </div>
          <div className="quiz-step" key={stepIdx}>
            <div className="quiz-eyebrow">{step.eyebrow}</div>
            <h1 className="quiz-title">{step.title}</h1>
            <p className="quiz-desc">{step.desc}</p>
            <div className="quiz-options" role="radiogroup" aria-label={step.title}>
              {step.options.map(opt => (
                <button key={String(opt.v)} role="radio" aria-checked={selected === opt.v} className={`quiz-option ${selected === opt.v ? 'selected' : ''}`} onClick={() => select(opt.v)} data-testid={`opt-${step.key}-${opt.v}`}>
                  <div><strong>{opt.label}</strong><small>{opt.sub}</small></div>
                  <span className="quiz-option-icon">{selected === opt.v ? '✓' : '→'}</span>
                </button>
              ))}
            </div>
            <div className="quiz-buttons">
              <button className="btn-secondary" onClick={back} disabled={stepIdx === 0} data-testid="quiz-back">← Back</button>
              <button className="btn-primary" onClick={next} disabled={!canProceed} data-testid="quiz-next">{stepIdx === STEPS.length - 1 ? 'See my matches' : 'Next'}</button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </RevealProvider>
  );
}
